import type { Provider } from '@openx/types/provider.ts';
import type { DataTypeFilterKeys, DataTypeFilters } from '../../../../../../../modules/audiences/AudienceForm/types.ts';
import { type ExportTypeValue, SELECTIVE_SHARING } from '../../../../constants.ts';
import type { SegmentFiltersQueryParams } from '../../components/drawer/components/SegmentsList/types.ts';
import { and, buildRangeFilter, eq, ilike, in_, neq, or, type WhereCondition } from './filterHelpers.ts';

const dataTypeFilterKeys: DataTypeFilterKeys[] = [
  'past_browsing_interests',
  'lifestyles',
  'location_visits',
  'shopping',
  'advanced_demographics',
  'attitudes',
  'marketplace',
  'purchase_propensity',
];

const propensityMap = {
  high: 'H',
  medium: 'M',
  low: 'L',
} as const;

const segmentTypeMap = {
  lifestyles: 'Lifestyles',
  shopping: 'Shopping',
  advanced_demographics: 'Advanced Demographics',
  location_visits: 'Location Visits',
  attitudes: 'Attitudes',
  purchase_propensity: 'Purchase Propensity',
} as const;

export const buildPhraseFilter = (filters: SegmentFiltersQueryParams): WhereCondition => {
  if (!filters.phrase) return undefined;

  const clause = or(
    ilike('name', filters.phrase),
    ilike('description', filters.phrase),
    ilike('segment_type', filters.phrase),
    ilike('segment_category', filters.phrase),
    ilike('segment_sub_category', filters.phrase)
  );

  return and(clause);
};

export const buildRangeFilters = (filters: SegmentFiltersQueryParams): WhereCondition[] => {
  const clauses: WhereCondition[] = [];
  if (filters.reach) clauses.push(buildRangeFilter('reach', filters.reach));
  if (filters.pricePom)
    clauses.push(
      buildRangeFilter('computed_price_pom', {
        min: filters.pricePom.min != null ? +filters.pricePom.min / 100 : undefined,
        max: filters.pricePom.max != null ? +filters.pricePom.max / 100 : undefined,
      })
    );
  if (filters.priceCpm) clauses.push(buildRangeFilter('computed_price_cpm', filters.priceCpm));

  return clauses.filter(Boolean).map(clause => and(clause));
};

export const buildCategoryFilters = (filters: SegmentFiltersQueryParams): WhereCondition[] => {
  const clauses: WhereCondition[] = [];
  if (filters.category) clauses.push(in_('segment_category', filters.category));
  if (filters.subCategory) clauses.push(in_('segment_sub_category', filters.subCategory));

  return clauses.filter(Boolean).map(clause => and(clause));
};

export const buildDataTypeFilters = (
  filters: SegmentFiltersQueryParams,
  accountId: string,
  isOaMatchType: boolean
): WhereCondition[] => {
  const dt: DataTypeFilters = {
    past_browsing_interests: filters.past_browsing_interests,
    lifestyles: filters.lifestyles,
    location_visits: filters.location_visits,
    shopping: filters.shopping,
    advanced_demographics: filters.advanced_demographics,
    purchase_propensity: filters.purchase_propensity,
    attitudes: filters.attitudes,
    marketplace: filters.marketplace,
  };

  const clauses: WhereCondition[] = [];

  if (!dataTypeFilterKeys.some(key => filters[key] !== undefined)) return clauses;

  // lifestyles
  if (dt.lifestyles) {
    const clause: WhereCondition[] = [eq('category', 'predefined'), eq('segment_type', segmentTypeMap.lifestyles)];

    if (dt.lifestyles !== 'all') clause.push(eq('propensity', propensityMap[dt.lifestyles]));

    clauses.push(and(...clause));
  }

  // shopping
  if (dt.shopping) {
    const clause: WhereCondition[] = [eq('category', 'predefined'), eq('segment_type', segmentTypeMap.shopping)];

    if (dt.shopping !== 'all') clause.push(eq('propensity', propensityMap[dt.shopping]));

    clauses.push(and(...clause));
  }

  // advanced_demographics
  if (dt.advanced_demographics) {
    const clause: WhereCondition[] = [
      eq('category', 'predefined'),
      eq('segment_type', segmentTypeMap.advanced_demographics),
    ];

    if (dt.advanced_demographics !== 'all') clause.push(eq('propensity', propensityMap[dt.advanced_demographics]));

    clauses.push(and(...clause));
  }

  // location_visits
  if (dt.location_visits) {
    const clause: WhereCondition[] = [eq('category', 'predefined'), eq('segment_type', segmentTypeMap.location_visits)];

    clauses.push(and(...clause));
  }

  // purchase_propensity
  if (dt.purchase_propensity) {
    const clause: WhereCondition[] = [
      eq('category', 'predefined'),
      eq('segment_type', segmentTypeMap.purchase_propensity),
    ];

    if (dt.purchase_propensity !== 'all') clause.push(eq('propensity', propensityMap[dt.purchase_propensity]));

    clauses.push(and(...clause));
  }

  // past_browsing_interests
  if (dt.past_browsing_interests) {
    const clause: WhereCondition[] = [eq('category', 'predefined'), eq('sub_category', 'past_browsing_interests')];

    clauses.push(and(...clause));
  }

  // attitudes
  if (dt.attitudes) {
    const clause: WhereCondition[] = [eq('category', 'predefined'), eq('segment_type', segmentTypeMap.attitudes)];

    clauses.push(and(...clause));
  }

  // marketplace
  if (dt.marketplace && isOaMatchType) {
    clauses.push(
      and(
        neq('account_id', accountId),
        or(eq('sellable', true), and(eq('type', '1p'), eq('sharing_type', SELECTIVE_SHARING)))
      )
    );
  }

  return clauses.filter(Boolean);
};

export const buildPartnerProviderFilters = (filters: SegmentFiltersQueryParams): WhereCondition[] => {
  const clauses: WhereCondition[] = [];
  if (filters.dataPartner && Array.isArray(filters.dataPartner) && filters.dataPartner.length > 0) {
    clauses.push(in_('account_id', filters.dataPartner));
  }

  if (filters.dataProvider && Array.isArray(filters.dataProvider) && filters.dataProvider.length > 0) {
    clauses.push(in_('provider_id', filters.dataProvider));
  }

  return clauses.filter(Boolean).map(clause => or(clause));
};

export const buildDefaultGroups = (accountId: string, providerId?: string, isOaMatchType = true) => {
  if (providerId && !isOaMatchType) {
    return [
      { category: 'direct', provider_id: providerId, account_id: accountId },
      { category: 'direct', account_id: accountId, sub_category: 'uploaded' },
    ];
  }

  return [
    { category: 'predefined' },
    { sellable: true, account_id: neq(accountId) },
    { category: 'direct', account_id: accountId },
    { type: '1p', sharing_type: SELECTIVE_SHARING },
  ];
};

export const buildShowMySegmentsFilters = (accountId: string, linkedProviders: Provider[]): WhereCondition[] => {
  const linkedProviderIds: string[] = linkedProviders.map(provider => provider.id);

  return [
    and(
      eq('account_id', accountId),
      eq('category', 'direct'),
      or(or(in_('provider_id', linkedProviderIds), eq('sub_category', 'uploaded')))
    ),
  ];
};

export const buildExportTypeFilter = (exportType?: ExportTypeValue | null): WhereCondition | undefined => {
  if (!exportType) return undefined;

  return {
    provider: {
      allowed_export_types: {
        _contains: [exportType],
      },
    },
  };
};
export const buildShowOXSProprietarySegmentsFilters = (): WhereCondition[] => {
  return [eq('category', 'predefined')];
};

export const buildThirdPartyDataSegmentsFilters = (accountId: string): WhereCondition[] => {
  return [
    and(
      neq('account_id', accountId),
      or(eq('sellable', true), and(eq('type', '1p'), eq('sharing_type', SELECTIVE_SHARING)))
    ),
  ];
};
